import React, { useEffect } from 'react';
import Localize from 'react-intl-universal';

import { Field, Form, FieldArray, useFormikContext } from 'formik';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import SelectInput from '@components/SelectInput/SelectInput';

const StyledHeader = styled(Typography)(({ theme }) => ({
  paddingTop: 0,
  fontSize: '1.7rem',
  fontWeight: 700,
  color: theme.palette.booking.header
}));

const StyledSubheader = styled(Box)(({ theme }) => ({
  marginBottom: '1.5rem',
  color: theme.palette.grey[600],
  fontWeight: '400',
  fontSize: '0.85rem'
}));

const StyledGrid = styled(Grid)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: '7px',
  marginBottom: '5px'
}));

const CreateFormArray = ({
  values,
  setIsFormValid,
  setFieldValue,
  bookingEventDiscountsOptions,
  discountOptions,
  optionalSessions = []
}) => {
  const { submitForm, isValid } = useFormikContext();

  useEffect(() => {
    setIsFormValid(isValid);
  }, [isValid]);

  useEffect(() => {
    return () => {
      submitForm();
    };
  }, []);

  const paddingBasedOnScreenSize = {
    paddingLeft: { xs: '5px !important', sm: '16px' },
    paddingRight: { xs: '5px', sm: '16px' }
  };

  const eventWithDiscountsCount = bookingEventDiscountsOptions
    ? bookingEventDiscountsOptions.filter((item) => item?.count !== 0).length
    : 0;

  const sessionsWithDiscountsCount = discountOptions?.optionalSessions
    ? discountOptions.optionalSessions.filter(
        (session) =>
          optionalSessions.some((opt) => opt?.id === session?.sessionId) &&
          session?.discounts?.some((discount) => discount?.count !== 0)
      ).length
    : 0;

  return (
    <Form>
      <FieldArray name='discounts'>
        {() => (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', padding: 2, gap: 5 }}>
              {values.discounts.length > 0 &&
                values.discounts.map((value, index) => {
                  return (
                    <StyledGrid
                      key={index}
                      container
                      spacing={2}
                      padding={{ xs: '5px', sm: '16px' }}
                    >
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        sx={{ ...paddingBasedOnScreenSize, pt: '0px !important' }}
                      >
                        <StyledHeader>{value?.name || ''}</StyledHeader>
                        <StyledSubheader> {Localize.get('Booking.participant')}</StyledSubheader>
                      </Grid>
                      {!!eventWithDiscountsCount && (
                        <>
                          <Grid
                            item
                            xs={12}
                            sm={12}
                            sx={{
                              ...paddingBasedOnScreenSize,
                              mb: '-1rem !important',
                              paddingTop: '0px !important'
                            }}
                          >
                            <Typography variant='h6'>
                              {Localize.get('Booking.eventDiscount')}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={5} sx={{ ...paddingBasedOnScreenSize, mb: 1 }}>
                            <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                              <Field
                                name={`discounts.${index}.eventDiscount`}
                                component={SelectInput}
                                options={bookingEventDiscountsOptions}
                                onChange={(e) => {
                                  setFieldValue(`discounts.${index}.eventDiscount`, e.target.value);
                                  submitForm();
                                }}
                              />
                            </FormControl>
                          </Grid>
                        </>
                      )}
                      {value?.optionalSessionsDiscounts?.length > 0 &&
                        !!sessionsWithDiscountsCount && (
                          <>
                            <Grid item xs={12} sm={12} sx={{ ...paddingBasedOnScreenSize, mb: 1 }}>
                              <Typography variant='h6'>
                                {sessionsWithDiscountsCount === 1
                                  ? Localize.get('Booking.optionalSessionDiscount')
                                  : Localize.get('Booking.optionalSessionsDiscount')}
                              </Typography>
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              sm={12}
                              sx={{ ...paddingBasedOnScreenSize, mb: 1, rowGap: '1.3rem' }}
                              container
                            >
                              <FieldArray name={`discounts.${index}.optionalSessionsDiscounts`}>
                                <>
                                  {value?.optionalSessionsDiscounts?.length > 0 &&
                                    value?.optionalSessionsDiscounts.map(
                                      (optionalDiscount, optionalIndex) => {
                                        const tempSessionDiscounts =
                                          discountOptions?.optionalSessions?.find(
                                            (item) => item.sessionId === optionalDiscount?.sessionId
                                          )?.discounts || [];

                                        return tempSessionDiscounts.length > 1 ? (
                                          <Grid
                                            key={optionalIndex}
                                            item
                                            xs={12}
                                            sm={5}
                                            sx={{ ...paddingBasedOnScreenSize, mb: 1 }}
                                          >
                                            <FormControl
                                              fullWidth
                                              size='small'
                                              sx={{ alignItems: null }}
                                            >
                                              <Field
                                                name={`discounts.${index}.optionalSessionsDiscounts.${optionalIndex}`}
                                                label={
                                                  optionalDiscount?.sessionName ||
                                                  'Booking.optionalSessionDiscount'
                                                }
                                                component={SelectInput}
                                                options={tempSessionDiscounts}
                                                value={optionalDiscount?.value}
                                                onChange={(e) => {
                                                  setFieldValue(
                                                    `discounts.${index}.optionalSessionsDiscounts.${optionalIndex}`,
                                                    {
                                                      ...optionalDiscount,
                                                      value: e.target.value
                                                    }
                                                  );
                                                  submitForm();
                                                }}
                                              />
                                            </FormControl>
                                          </Grid>
                                        ) : null;
                                      }
                                    )}
                                </>
                              </FieldArray>
                            </Grid>
                          </>
                        )}
                    </StyledGrid>
                  );
                })}
            </Box>
          </>
        )}
      </FieldArray>
    </Form>
  );
};
export default CreateFormArray;
